import { RiCheckboxCircleLine } from "react-icons/ri";
import {
  EventFragment,
  UpdateEventDocument,
} from "../../../../__generated__/graphql";
import { useState } from "react";
import Modal from "../../../Modal";
import { useMutation } from "@apollo/client";
import Button from "../../../Button";
import { cn } from "../../../../lib/utils";
import { toast } from "react-hot-toast";
import DraftBadge from "../../../DraftBadge";

interface EventDraftButtonProps {
  event: EventFragment | null;
  variant?: "default" | "small";
  readonly?: boolean;
}

export default function EventDraftButton({
  event,
  variant = "default",
  readonly = false,
}: EventDraftButtonProps) {
  const [isDraftDialogOpen, setIsDraftDialogOpen] = useState(false);
  const [updateEventMutation, { loading: isUpdatingEvent }] =
    useMutation(UpdateEventDocument);

  if (!event) return null;

  return (
    <>
      <Modal
        isModalOpen={isDraftDialogOpen}
        onClose={() => setIsDraftDialogOpen(false)}
        title="Publier l'évènement"
        description="Vous pouvez le publier en cliquant sur le bouton ci-dessous. Tous les participants seront notifiés."
        className="max-w-[500px]"
      >
        <div className="flex justify-between items-center">
          <Button
            variant="secondary"
            onClick={() => setIsDraftDialogOpen(false)}
          >
            Annuler
          </Button>
          <Button
            isLoading={isUpdatingEvent}
            variant="primary"
            onClick={() =>
              updateEventMutation({
                variables: {
                  updateEventInput: { id: event.id, isDraft: false },
                },
                onCompleted: () => {
                  setIsDraftDialogOpen(false);
                  toast.success("Évènement publié avec succès");
                },
                onError: (error) => {
                  toast.error(
                    error instanceof Error
                      ? error.message
                      : "Une erreur est survenue lors de la publication de l'évènement"
                  );
                },
              })
            }
          >
            Publier
          </Button>
        </div>
      </Modal>

      {event.isDraft ? (
        <div
          className={cn(
            "cursor-pointer hover:bg-yellow-500/20 transition-all duration-300 rounded-full w-fit",
            readonly && "pointer-events-none"
          )}
          onClick={readonly ? undefined : () => setIsDraftDialogOpen(true)}
        >
          <DraftBadge
            variant={variant === "small" ? "small" : "default"}
            className={cn(variant === "default" && "px-4 py-1 gap-2")}
          />
        </div>
      ) : (
        <div
          className={cn(
            "flex items-center gap-2 text-green-500 bg-green-500/10 rounded-full px-4 py-1 w-fit text-sm",
            variant === "small" && "text-xs py-1 px-2"
          )}
        >
          <RiCheckboxCircleLine
            className={cn("w-4 h-4", variant === "small" && "w-3 h-3")}
          />
          <span className={cn("text-sm", variant === "small" && "text-xs")}>
            Publié
          </span>
        </div>
      )}
    </>
  );
}
